/**
 * @module components/analysis/signalDefinitions
 * @description
 *   Plain-language tooltip copy for View Signals and Penalties.
 *   Keys match `server/python_analysis/scoring.py` contributor / penalty ids.
 *   Lyrics merges use `text_` / `delivery_` prefixes (resolved in getters).
 *
 * @see docs/SCORE_BREAKDOWN.md
 * @see client/src/components/analysis/ScoreGrid.js
 */

const SIGNAL_DEFINITIONS = {
  // Production
  clarity:
    'How bright and clear the sound is. Too dull or overly harsh pulls this out of the sweet spot.',
  tonal_balance:
    'How evenly pitched musical content sits versus noise-like texture. More balanced tone usually scores higher.',
  dynamics:
    'How much loudness and energy move over time. A healthy amount of movement typically scores best.',
  spectral_interest:
    'How much brightness shifts across the track. Some variation keeps the sound engaging; extremes feel static or chaotic.',
  bandwidth:
    'How wide the frequency spread is. Too narrow sounds thin; too wide can feel unfocused or harsh.',
  headroom:
    'How much room remains above average loudness before peaks hit the ceiling. Healthy headroom avoids a crushed sound.',

  // Mixing
  frequency_balance:
    'Whether high-frequency energy sits in a practical range—neither too dark nor overly bright and brittle.',
  tonal_clarity:
    'How clear and pitched the mix sounds versus washed-out or noisy. Cleaner tone usually scores higher.',
  loudness:
    'Overall loudness level relative to a practical commercial range. Too quiet or too slammed both hurt.',
  consistency:
    'How steady levels stay without wild jumps. Uneven volume across the track pulls this down.',

  // Emotion
  tempo:
    'Whether tempo sits in a range that often feels emotionally engaging. Extremely slow or frantic feels can score lower.',
  harmonic:
    'How rich and varied the harmonic content is. More interesting harmony usually reads as more expressive.',
  rhythmic:
    'How intricate the rhythmic patterns are. Moderate complexity tends to feel more alive than flat or overly busy.',
  energy:
    'Overall energy level of the track. Enough drive keeps emotion present without needing to be maxed out.',
  rhythm_strength:
    'How strong and defined the rhythmic pulse is. A clearer groove usually supports emotional impact.',
  // Emotion uses "Dynamic movement" with id dynamics — shared key above covers it.
  minor_mood:
    'A small boost when the track is in a minor key, which often reads as more emotive or dramatic.',

  // Vocals (primary vocal_analysis path)
  pitch_stability:
    'How steady the sung pitch stays. Less unintended wavering usually means stronger control.',
  expressiveness:
    'How much pitch and energy variation add feeling to the performance. Flat delivery scores lower.',
  vibrato:
    'Natural vibrato amount. A moderate amount typically helps; little or excessive vibrato scores lower.',
  vocal_presence:
    'How prominently the vocal sits in the mix relative to the backing. Buried or overpowering vocals both hurt.',
  // Vocals also uses id "clarity" with label "Vocal clarity" — same brightness/clarity idea applied to the voice.
  voiced_ratio:
    'How much of the vocal region is actual voiced singing versus silence or non-voiced noise.',

  // Vocals (fallback path without vocal_analysis)
  pitch_consistency:
    'How consistent detected pitch is across the track. Unstable pitch usually lowers the vocal score.',
  pitch_presence:
    'Whether a clear pitched, vocal-like signal is present in a typical singing range.',
  pitch_range:
    'How wide the pitch span is. A moderate expressive range usually scores best.',
  vocal_band:
    'Whether spectral energy sits in a frequency range typical for vocals.',
  vocal_energy:
    'How much energy is concentrated in vocal-like regions of the mix.',

  // Lyrics text
  length:
    'How substantial the lyric text is. Very short or sparse lyrics tend to score lower.',
  vocabulary:
    'How varied the word choices are relative to total length. More variety usually scores higher.',
  word_depth:
    'Average word length as a rough stand-in for lexical richness—neither too simple nor overly dense.',

  // Lyrics delivery
  syllable_density:
    'How densely syllables are packed into the vocal. Moderate density usually reads as clearer delivery.',
  vocal_activity:
    'How much of the track has active vocal content versus long gaps or sparse singing.',
  articulation:
    'How distinctly consonants and syllable edges come through in the performance.',
  dynamic_phrasing:
    'How much the vocal loudness rises and falls to shape phrases. Flat phrasing scores lower.',
};

/** Penalty ids from scoring.py (separate from contributor ids — `consistency` is used in both). */
const PENALTY_DEFINITIONS = {
  consistency:
    'Loudness jumps around more than ideal across the track. Points are subtracted for that unevenness.',
  clipping:
    'Peaks sit much louder than the average level, which can mean distortion or limited headroom. Points are subtracted when this shows up.',
};

/**
 * @param {string} id
 * @returns {string|null}
 */
export function getSignalDefinition(id) {
  if (!id) return null;
  if (SIGNAL_DEFINITIONS[id]) return SIGNAL_DEFINITIONS[id];

  // Lyrics merge prefixes from scoring.py
  if (id.startsWith('text_')) {
    return SIGNAL_DEFINITIONS[id.slice(5)] || null;
  }
  if (id.startsWith('delivery_')) {
    return SIGNAL_DEFINITIONS[id.slice(9)] || null;
  }

  return null;
}

/**
 * @param {string} id
 * @returns {string|null}
 */
export function getPenaltyDefinition(id) {
  if (!id) return null;
  return PENALTY_DEFINITIONS[id] || null;
}
